import React, { useEffect, useState } from "react";
import { getSessionParam } from "services/sessionParamsService";
import { ALL_SCOPES } from "statics/sessionScopes";
import { find, includes, isEmpty, snakeCase, toUpper } from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import type { ScopeTypes } from "types/graphql";

import queryString from "query-string";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import SessionlessAnimation from "components/SessionlessAnimation";
import IconAuthlessErrorState from "./IconAuthlessErrorState";

function getSessionScopeFromUrl() {
  const path = window.location.pathname;
  const pathWithoutSession = path.replace("/session/", "");
  const snakedPath = toUpper(snakeCase(pathWithoutSession));
  const sessionScope = find(ALL_SCOPES, (scope) => includes(snakedPath, scope));
  return sessionScope;
}

// This HOC reads no parent-supplied props by name (it only consumes the
// redux-injected `push` and the withApollo-injected `client`); parent props
// are forwarded untouched via the `{...props}` spread onto WrappedComponent.
// OwnProps is therefore empty, so the props are exactly PropsFromRedux.

const mapDispatchToProps = {
  push,
};

const connector = connect(null, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type WithSessionTokenProps = PropsFromRedux;

export default function withSessionToken(
  WrappedComponent: React.ComponentType<Record<string, unknown>>
) {
  const Component = (
    props: WithApolloClient<Omit<WithSessionTokenProps, "client">>
  ) => {
    const [sessionParams, setSessionParams] = useState<Record<string, unknown>>(
      {}
    );
    const [isFetching, setIsFetching] = useState(true);
    const [showErrorState, setShowErrorState] = useState(false);

    // @ts-expect-error -- pre-existing bug: passing an async function to
    // useEffect returns a Promise rather than a cleanup fn; preserved as-is.
    useEffect(async () => {
      const sessionScope = getSessionScopeFromUrl();
      const { token, identityHash } = queryString.parse(window.location.search);
      const tokenMissing = isEmpty(token) || isEmpty(identityHash);

      if (tokenMissing) {
        setShowErrorState(true);
        setIsFetching(false);
        return;
      }

      const sessionParam = await getSessionParam(
        // client is optional in withApollo's types; cast at the call boundary
        props.client as ApolloClient<object>,
        {
          // query-string yields string | string[] | null and getSessionScopeFromUrl
          // yields string | undefined; the runtime contract narrows these to the
          // GraphQL variable types — cast at the boundary as the JS did implicitly.
          sessionToken: token as string,
          sessionScope: sessionScope as ScopeTypes,
          identityHash: identityHash as string,
        }
      );

      if (isEmpty(sessionParam)) {
        setShowErrorState(true);
        setIsFetching(false);

        return;
      }
      setSessionParams({
        ...sessionParam.sessionParams,
        sessionScope,
        identityHash,
        sessionToken: token,
      });

      const { success, error } = sessionParam;

      const erroredSession = success === "false" || !isEmpty(error);
      if (erroredSession) {
        setShowErrorState(true);
        setIsFetching(false);

        return;
      }

      setIsFetching(false);
    }, []);

    function goToDashboard() {
      props.push("/dashboard");
    }

    if (showErrorState) {
      // dashboard will force login or show dashboard if they have a previous session
      setTimeout(goToDashboard, 5000);
      return (
        <>
          <IconAuthlessErrorState
            header={"Something went wrong validating your session token. "}
          />
        </>
      );
    }
    return !isFetching ? (
      <WrappedComponent {...props} sessionParams={sessionParams} />
    ) : (
      <>
        <SessionlessAnimation />
        <div className="authlessAnimation">
          <IconHeader variant="referralHeader" headerText="Preparing Preview" />
          <IconSubheader subheader="Please review and submit your payroll on the next screen." />
        </div>
      </>
    );
  };

  return connector(withApollo<WithSessionTokenProps>(Component));
}
